import { DeleteOutlined } from "@ant-design/icons";
import { Button, Flex, List, Modal, Select, Typography, message } from "antd";
import type React from "react";
import { useEffect, useState } from "react";
import { useAppSelector } from "../../hooks/redux-hooks";
import useSpecialityReference from "../../hooks/useSpecialityReference";
import { getClientShares, revokeClientShare, shareClient } from "../../service/client.service";
import { membersService } from "../../service/members.service";
import type { Member } from "../../service/members.service";
import type { TClientForm, TClientShare } from "../../types/index.type";
import styles from "./share-client-modal.module.scss";

interface ShareClientModalProps {
  visible: boolean;
  onClose: () => void;
  client: TClientForm;
  onShareSuccess?: () => void;
}

const ShareClientModal: React.FC<ShareClientModalProps> = ({ visible, onClose, client, onShareSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [groupMembers, setGroupMembers] = useState<Member[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [existingShares, setExistingShares] = useState<TClientShare[]>([]);
  const [loadingShares, setLoadingShares] = useState(false);
  const [revokingUserId, setRevokingUserId] = useState<number | null>(null);
  const userData = useAppSelector((state) => state.user.data);
  const { referIndividualBy } = useSpecialityReference();
  // Fetch group members and existing shares
  useEffect(() => {
    if (visible && client.id) {
      fetchGroupMembers();
      fetchExistingShares();
    }
  }, [visible, client.id]);

  const fetchGroupMembers = async () => {
    try {
      const response = await membersService.getMembers();
      // Filter out the current client owner
      const members = response.results || [];
      setGroupMembers(members);
    } catch (error) {
      console.error("Error fetching group members:", error);
      message.error(`Sharing ${referIndividualBy.toLowerCase()}s is only available for group users`);
    }
  };

  const fetchExistingShares = async () => {
    if (!client.id) return;

    setLoadingShares(true);
    try {
      const response = await getClientShares(client.id);
      setExistingShares(response.data);
    } catch (error) {
      console.error("Error fetching existing shares:", error);
      // Don't show error message for 404 - it just means no shares exist yet
    } finally {
      setLoadingShares(false);
    }
  };

  const handleShare = async () => {
    if (!client.id || selectedUserIds.length === 0) {
      message.warning("Please select at least one member to share with");
      return;
    }

    setLoading(true);
    try {
      const response = await shareClient(client.id, selectedUserIds);

      if (response.data.created > 0) {
        message.success(`Successfully shared with ${response.data.created} member(s)`);
      }

      if (response.data.already_shared && response.data.already_shared.length > 0) {
        message.info(`${response.data.already_shared.length} member(s) already had access`);
      }

      if (response.data.invalid_users && response.data.invalid_users.length > 0) {
        message.warning(`${response.data.invalid_users.length} member(s) are not in your group`);
      }

      // Refresh the existing shares list
      await fetchExistingShares();

      // Clear selection
      setSelectedUserIds([]);

      // Call success callback
      if (onShareSuccess) {
        onShareSuccess();
      }
    } catch (error) {
      console.error("Error sharing client:", error);
      message.error("Failed to share client");
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (userId: number) => {
    if (!client.id) return;

    setRevokingUserId(userId);
    try {
      await revokeClientShare(client.id, userId);
      message.success("Access revoked successfully");

      // Refresh the existing shares list
      await fetchExistingShares();

      // Call success callback
      if (onShareSuccess) {
        onShareSuccess();
      }
    } catch (error) {
      console.error("Error revoking access:", error);
      message.error("Failed to revoke access");
    } finally {
      setRevokingUserId(null);
    }
  };

  const handleClose = () => {
    setSelectedUserIds([]);
    onClose();
  };

  // Filter out members who already have access and the owner
  const existingShareUserIds = existingShares.map((share) => share.shared_with);
  const availableMembers = groupMembers.filter(
    (member) => member.id && !existingShareUserIds.includes(member.id) && member.id !== userData?.id,
  );
  return (
    <Modal
      title={`Share "${client.name}"`}
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={600}
      destroyOnClose
    >
      <Flex vertical gap={20} className={styles.container}>
        {/* Share with new members */}
        <Flex vertical gap={10}>
          <Typography.Text strong>Share with members:</Typography.Text>
          <Select
            mode="multiple"
            placeholder="Select members to share with"
            value={selectedUserIds}
            onChange={setSelectedUserIds}
            style={{ width: "100%" }}
            optionFilterProp="label"
            options={availableMembers.map((member) => ({
              label: `${member.name} (${member.email})`,
              value: member.id,
            }))}
            disabled={availableMembers.length === 0}
          />
          {availableMembers.length === 0 && (
            <Typography.Text type="secondary" style={{ fontSize: "12px" }}>
              All group members already have access to this client
            </Typography.Text>
          )}
          <Button
            type="primary"
            onClick={handleShare}
            loading={loading}
            disabled={selectedUserIds.length === 0 || availableMembers.length === 0}
          >
            Share
          </Button>
        </Flex>

        {/* List of current shares */}
        <Flex vertical gap={10}>
          <Typography.Text strong>Members with access ({existingShares.length}):</Typography.Text>
          {loadingShares ? (
            <Typography.Text type="secondary">Loading...</Typography.Text>
          ) : existingShares.length > 0 ? (
            <List
              className={styles.sharesList}
              dataSource={existingShares}
              renderItem={(share) => (
                <List.Item
                  actions={[
                    <Button
                      key="revoke"
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleRevoke(share.shared_with)}
                      loading={revokingUserId === share.shared_with}
                    >
                      Revoke
                    </Button>,
                  ]}
                >
                  <List.Item.Meta title={share.shared_with_name} description={share.shared_with_email} />
                </List.Item>
              )}
            />
          ) : (
            <Typography.Text type="secondary">This client has not been shared with anyone yet</Typography.Text>
          )}
        </Flex>
      </Flex>
    </Modal>
  );
};

export default ShareClientModal;
